import { apiClient } from '@app/api';
import { useAuth0 } from '@auth0/auth0-react';
import { SplashLoader } from '@mi-front/ui';
import debugFn from 'debug';
import { ReactNode, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

const debug = debugFn('auth:TokenGate');

type Props = { children: (token: string | null) => ReactNode };

export function TokenGate({ children }: Props) {
  const { isLoading, getAccessTokenSilently, error: auth0Error } = useAuth0();
  const history = useHistory();

  const [token, setToken] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [hardFail, setHardFail] = useState<Error | null>(null);

  useEffect(() => {
    if (isLoading) {
      debug('Auth0 SDK still loading...');
      return;
    }

    let mounted = true;
    debug('Auth0 SDK ready, starting silent token check.');

    (async () => {
      setChecking(true);
      try {
        debug('Attempting getAccessTokenSilently()');

        const t = await getAccessTokenSilently({
          // You can specify audience/scope if needed:
          // authorizationParams: { audience: 'https://api.example.com', scope: 'read:stuff' },
          // cacheMode: 'on', // or 'off' for forced refresh
        });

        if (!mounted) {
          debug('Component unmounted before token resolved.');
          return;
        }

        debug('Token retrieved successfully.');
        apiClient.setToken(t);
        setToken(t);
        setHardFail(null);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        const code = e?.error || e?.code;
        debug('Error fetching token silently:', e);

        const isMissingRefreshToken =
          e?.message?.includes('Missing Refresh Token') || code === 'missing_refresh_token';

        if (
          code === 'login_required' ||
          code === 'consent_required' ||
          code === 'interaction_required' ||
          // A cached refresh token was rejected (expired, revoked, or invalidated by
          // rotation under concurrent refreshes). Auth0 recommends triggering a fresh
          // login in this case when the iframe fallback is disabled.
          code === 'invalid_grant' ||
          isMissingRefreshToken
        ) {
          const { pathname, search } = history.location;
          const params = new URLSearchParams(search);
          const hasRedirectTo = params.has('redirect_to');

          debug(
            `Silent auth failed with ${code}; handling redirect. onLoginPage=${
              pathname === '/login'
            } hasRedirectTo=${hasRedirectTo}`,
          );

          // If already on /login or /logout, do not add a default redirect_to; just leave as-is
          if (pathname === '/login' || pathname === '/logout') {
            return;
          }

          const currentPath = pathname + search;
          const redirectTo = currentPath || '/';
          if (!redirectTo || redirectTo === '/') {
            history.push('/login');
          } else {
            history.push(`/login?redirect_to=${encodeURIComponent(redirectTo)}`);
          }

          return; // redirect to login page
        }

        const err = e instanceof Error ? e : new Error(String(e));
        debug('Non-recoverable silent auth error:', err);
        setHardFail(err);
      } finally {
        if (mounted) {
          debug('Silent auth flow finished (success or failure).');
          setChecking(false);
        }
      }
    })();

    return () => {
      mounted = false;
      debug('TokenGate unmounted, aborting any in-flight token request.');
    };
  }, [isLoading, getAccessTokenSilently, history]);

  // Loading or checking → show splash
  if (isLoading || checking) {
    return <SplashLoader />;
  }

  if (hardFail || auth0Error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const msg = hardFail?.message || (auth0Error as any)?.message || 'Authentication error';
    debug('Rendering auth error UI:', msg);
    return (
      <div style={{ padding: 16 }}>
        <FormattedMessage id="auth.tokenGate.errorLabel" defaultMessage="Auth error:" /> {msg}
      </div>
    );
  }

  debug(`Token fetch ready (${token ? 'success' : 'failure'}), rendering children.`);

  return <>{children(token)}</>;
}
